import React, { useEffect, useState, useMemo } from 'react';
import './Profiles.css';
import Spinner from './Spinner';
import axios from 'axios';
import DataTable, { DataTableColumn, ActionMenu, DataTableAction } from './DataTable';
import { Icons } from './ActionIcons';

type Profile = { _id?: string; name: string; description?: string; isActive?: boolean; createdAt?: string; createdBy?: any; updatedAt?: string; modifiedBy?: any };
type Application = { _id?: string; name: string };
type ProfileApp = { _id?: string; profile: string | { _id?: string }; application: string | { _id?: string; name?: string } };

const Profiles: React.FC = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<{ [key: string]: string }>({});
  const [apps, setApps] = useState<Application[]>([]);
  const [links, setLinks] = useState<ProfileApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Profile | null>(null);
  const [creating, setCreating] = useState(false);
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());

  const loadAll = async () => {
    setLoading(true);
    const base = process.env.REACT_APP_API_URL || '';
    const url = base ? base.replace(/\/$/, '') : '';
    try {
      const [pRes, aRes, paRes] = await Promise.all([
        axios.get(`${url}/api/profiles`, { withCredentials: true }),
        axios.get(`${url}/api/applications`, { withCredentials: true }),
        axios.get(`${url}/api/profile-apps`, { withCredentials: true })
      ]);
      setProfiles(Array.isArray(pRes.data) ? pRes.data : []);
      setApps(Array.isArray(aRes.data) ? aRes.data : []);
      setLinks(Array.isArray(paRes.data) ? paRes.data : []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setProfiles([]); setApps([]); setLinks([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  // Resetar página quando filtro mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters]);

  // derived filtered + sorted list
  const sortedProfiles = React.useMemo(() => {
    let list = profiles.slice();
    
    // Filtros por coluna
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (filterValue.trim()) {
        const filterLower = filterValue.trim().toLowerCase();
        list = list.filter(p => {
          let cellValue = '';
          
          switch(columnKey) {
            case 'name':
              cellValue = (p.name || '').toLowerCase();
              break;
            case 'description':
              cellValue = (p.description || '').toLowerCase();
              break;
            case 'isActive':
              cellValue = p.isActive ? 'sim' : 'n\u00e3o';
              break;
            case 'createdBy':
            case 'modifiedBy':
              cellValue = p[columnKey] ? (typeof p[columnKey] === 'string' ? p[columnKey] as string : ((p[columnKey] as any).fullName || (p[columnKey] as any).username || '')).toLowerCase() : '';
              break;
            case 'createdAt':
            case 'updatedAt':
              cellValue = p[columnKey] ? new Date(p[columnKey] as string).toLocaleString().toLowerCase() : '';
              break;
            default:
              cellValue = '';
          }
          
          return cellValue.includes(filterLower);
        });
      }
    });
    
    if (sortColumn) {
      list.sort((a: any, b: any) => {
        const getVal = (row: any, c: string) => {
          if (c === 'createdBy' || c === 'modifiedBy') return row[c] ? (typeof row[c] === 'string' ? row[c] : (row[c].fullName || row[c].username || '')) : '';
          if (c === 'isActive') return row.isActive ? 1 : 0;
          return row[c] || '';
        };
        const va = getVal(a, sortColumn);
        const vb = getVal(b, sortColumn);
        if ((sortColumn === 'createdAt' || sortColumn === 'updatedAt') && va && vb) {
          return sortDir === 'asc' ? new Date(va).getTime() - new Date(vb).getTime() : new Date(vb).getTime() - new Date(va).getTime();
        }
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [profiles, sortColumn, sortDir, columnFilters]);

  // Dados paginados
  const paginatedProfiles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedProfiles.slice(startIndex, endIndex);
  }, [sortedProfiles, currentPage, pageSize]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const handleColumnFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  // theme is managed by AppLayout; local pages read it from localStorage for rendering

  // Função para gerar iniciais do nome
  const getInitials = (name: string | undefined): string => {
    if (!name) return '?';
    const words = name.trim().split(' ').filter(n => n.length > 0);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Função para gerar cor do avatar baseada no nome
  const getAvatarColor = (name: string | undefined): string => {
    if (!name) return '#6c757d';
    const colors = [
      '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
      '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };


  // Nota: autorizações são gerenciadas localmente em `selectedApps` durante edição

  // When editing, we will manage authorizations locally (selectedApps) and persist them only on save
  const initSelectedAppsForProfile = (profileId?: string) => {
    if (!profileId) { setSelectedApps(new Set()); return; }
    const set = new Set<string>();
    for (const a of apps) {
      const allowed = links.some(l => (typeof l.profile === 'string' ? l.profile === profileId : (l.profile as any)?._id === profileId) && (typeof l.application === 'string' ? l.application === a._id : (l.application as any)?._id === a._id));
      if (allowed && a._id) set.add(a._id);
    }
    setSelectedApps(set);
  };

  // CRUD profiles (small)
  const openCreate = () => { setEditing({ name: '', isActive: true }); setCreating(true); initSelectedAppsForProfile(undefined); };
  const openEdit = (p: Profile) => { setEditing(p); setCreating(false); initSelectedAppsForProfile(p._id); };
  const closeOverlay = () => setEditing(null);
  const saveProfile = async (p: Profile) => {
    const base = process.env.REACT_APP_API_URL || '';
    const url = base ? base.replace(/\/$/, '') : '';
    try {
      let profileId = p._id;
      if (p._id) {
        await axios.patch(`${url}/api/profiles/${p._id}`, p, { withCredentials: true });
      } else {
        const resp = await axios.post(`${url}/api/profiles`, p, { withCredentials: true });
        profileId = resp.data && resp.data._id ? resp.data._id : resp.data?.id || resp.data?._id;
      }

      if (!profileId) throw new Error('Profile id not available after save');

      // compute diffs between selectedApps and existing links for this profile
      const existingLinks = links.filter(l => (typeof l.profile === 'string' ? l.profile === profileId : (l.profile as any)?._id === profileId));
      const existingByApp: Record<string, ProfileApp> = {} as any;
      for (const el of existingLinks) {
        const appId = typeof el.application === 'string' ? el.application : (el.application as any)?._id;
        if (appId) existingByApp[appId] = el;
      }

      // create or delete as needed
      const toCreate: string[] = [];
      const toDelete: string[] = [];
      for (const a of apps) {
        if (!a._id) continue;
        const selected = selectedApps.has(a._id);
        const exists = !!existingByApp[a._id];
        if (selected && !exists) toCreate.push(a._id);
        if (!selected && exists) toDelete.push(existingByApp[a._id]._id || '');
      }

      // perform API calls
      await Promise.all([
        ...toCreate.map(appId => axios.post(`${url}/api/profile-apps`, { profile: profileId, application: appId }, { withCredentials: true })),
        ...toDelete.filter(id => id).map(id => axios.delete(`${url}/api/profile-apps/${id}`, { withCredentials: true }))
      ]);

      await loadAll();
      setEditing(null);
    } catch (err) { console.error('Erro salvar perfil', err); alert('Erro salvar perfil'); }
  };

  const deleteProfile = async (id?: string) => {
    if (!id) return; if (!window.confirm('Excluir perfil?')) return;
    const base = process.env.REACT_APP_API_URL || '';
    const url = base ? base.replace(/\/$/, '') : '';
    try { await axios.delete(`${url}/api/profiles/${id}`, { withCredentials: true }); await loadAll(); } catch (err) { console.error(err); alert('Erro excluir perfil'); }
  };

  // Define table columns
  const columns: DataTableColumn<Profile>[] = [
    {
      key: 'name',
      label: 'Nome',
      sortable: true,
      render: (value: any, row: Profile) => (
        <div className="user-name-cell">
          <div 
            className="user-avatar" 
            style={{ backgroundColor: getAvatarColor(value) }}
            title={value || ''}
          >
            {getInitials(value)}
          </div>
          <span>{value || ''}</span>
        </div>
      )
    },
    {
      key: 'isActive',
      label: 'Ativo',
      sortable: true,
      render: (value: any) => value ? 'Sim' : 'N\u00e3o'
    },
    {
      key: 'createdAt',
      label: 'Criado em',
      sortable: true,
      render: (value: any) => value ? new Date(value).toLocaleString() : ''
    },
    {
      key: 'createdBy',
      label: 'Criado por',
      sortable: true,
      render: (value: any) => value ? (typeof value === 'string' ? value : (value.fullName || value.username || '')) : ''
    },
    {
      key: 'updatedAt',
      label: 'Modificado em',
      sortable: true,
      render: (value: any) => value ? new Date(value).toLocaleString() : ''
    },
    {
      key: 'modifiedBy',
      label: '\u00daltimo modificador',
      sortable: true,
      render: (value: any) => value ? (typeof value === 'string' ? value : (value.fullName || value.username || '')) : ''
    },
    {
      key: 'actions',
      label: 'A\u00e7\u00f5es',
      sortable: false,
      render: (_: any, profile: Profile) => {
        const actions: DataTableAction[] = [
          {
            key: 'edit',
            label: 'Editar',
            icon: Icons.Pencil,
            onClick: () => openEdit(profile),
            variant: 'primary'
          },
          {
            key: 'delete',
            label: 'Excluir',
            icon: Icons.Trash,
            onClick: () => deleteProfile(profile._id),
            variant: 'danger'
          }
        ];
        
        return <ActionMenu actions={actions} />;
      }
    }
  ];

  // table columns for apps
  return (
    <>
      <div className="container py-5" style={{ marginTop: 80 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 style={{ marginBottom: 6 }}>Manutenção de Perfis</h1>
            <p className="lead" style={{ marginBottom: 0 }}>Defina perfis e autorizações de tiles (acesso por perfil).</p>
          </div>
          <div>
            <button 
              className="btn btn-primary" 
              onClick={openCreate} 
              aria-label="Criar perfil"
              style={{ padding: '0.5rem 1.25rem', fontWeight: 600 }}
            >
              Criar perfil
            </button>
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <DataTable
            columns={columns}
            data={paginatedProfiles}
            keyExtractor={(profile: Profile) => profile._id || profile.name}
            sortColumn={sortColumn}
            sortDirection={sortDir}
            onSort={handleSort}
            onFilter={handleColumnFilter}
            filters={columnFilters}
            striped
            hover
            responsive
            emptyMessage="Nenhum perfil encontrado"
            pagination={true}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={sortedProfiles.length}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[5, 10, 25, 50]}
          />
        )}

        {/* Overlay create/edit profile */}
        {editing && (
          <div className="users-overlay">
            <div className="users-overlay-box">
              <h4>{creating ? 'Criar perfil' : `Editar ${editing.name}`}</h4>
              <div className="mb-2">
                <label className="form-label">Nome</label>
                <input className="form-control" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="isActive" checked={!!editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} />
                <label className="form-check-label" htmlFor="isActive">Ativo</label>
              </div>
              <div className="mb-3">
                <label className="form-label">Autorizações (tiles)</label>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead>
                      <tr><th>Tile</th><th style={{ width: 120, textAlign: 'center' }}>Permitir</th></tr>
                    </thead>
                    <tbody>
                      {apps.map(a => (
                        <tr key={a._id}>
                          <td>{a.name}</td>
                          <td style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={!!(a._id && selectedApps.has(a._id))} onChange={() => {
                              if (!a._id) return;
                              const next = new Set(selectedApps);
                              if (next.has(a._id)) next.delete(a._id); else next.add(a._id);
                              setSelectedApps(next);
                            }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={closeOverlay}>Cancelar</button>
                <button className="btn profiles-save" onClick={() => saveProfile(editing)}>Salvar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profiles;
